import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '../../../components/ui';
import { Select } from '../../../components/ui/Select';
import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope } from '../../../widgets/types';

type PairingResult = { ok?: boolean; error?: string; code?: string; detail?: string; dry_run?: boolean;
  entity?: { id: string; label: string }; gitlab?: { id: number; username: string };
  provider?: string; extern_uid?: string; changed?: boolean };

export function IdentityPairing({ connection, scope }: { connection: string; scope: EffectiveScope }) {
  const { t } = useTranslation();
  const [entity, setEntity] = useState('');
  const [username, setUsername] = useState('');
  const [grant, setGrant] = useState('none');
  const [target, setTarget] = useState('');
  const [preview, setPreview] = useState<PairingResult | null>(null);
  const [result, setResult] = useState<PairingResult | null>(null);
  const reset = () => { setPreview(null); setResult(null); };
  const mutation = useMutation({ mutationFn: (confirm: boolean) => callWidgetTool<PairingResult>(
    'gitlab.identity_link', { connection_id: connection, entity, username, confirm,
      ...(grant !== 'none' ? { admin_grant: { kind: grant, ...(grant !== 'instance' ? { target } : {}) } } : {}),
    }, scope), onSuccess: (r, confirm) => { setResult(r); setPreview(!confirm && r.ok ? r : null); } });
  return <details className="border border-zinc-700 rounded p-3 space-y-2">
    <summary>{t('gitlab.pairing.title', 'Párování uživatelů a pověření správců')}</summary>
    <p className="text-xs text-zinc-400">{t('gitlab.pairing.help', 'Propoj účet ACM se skutečným účtem GitLabu. Správci ACM můžeš při párování výslovně udělit zvolený rozsah správy.')}</p>
    <div className="grid sm:grid-cols-2 gap-2">
      <Input value={entity} placeholder={t('gitlab.pairing.entity', 'Uživatel ACM (jméno nebo UUID)')} onChange={(e) => { reset(); setEntity(e.target.value); }} />
      <Input value={username} placeholder={t('gitlab.pairing.username', 'Přihlašovací jméno v GitLabu')} onChange={(e) => { reset(); setUsername(e.target.value); }} />
      <Select value={grant} onChange={(e) => { reset(); setGrant(e.target.value); }}>
        <option value="none">{t('gitlab.pairing.noGrant', 'Pouze spárovat')}</option>
        <option value="project">{t('gitlab.pairing.project', 'Správce projektu (Maintainer)')}</option>
        <option value="group">{t('gitlab.pairing.group', 'Vlastník skupiny (Owner)')}</option>
        <option value="instance">{t('gitlab.pairing.instance', 'Administrátor celé instance')}</option>
      </Select>
      {['project', 'group'].includes(grant) && <Input value={target} placeholder={t('gitlab.pairing.target', 'Cesta nebo ID projektu / skupiny')} onChange={(e) => { reset(); setTarget(e.target.value); }} />}
    </div>
    {preview?.entity && <p className="text-sm">{preview.entity.label} ↔ {preview.gitlab?.username} · {preview.provider} / {preview.extern_uid}</p>}
    {(mutation.error || result?.ok === false) && <p role="alert" className="text-red-400 text-sm">{mutation.error?.message ?? result?.error ?? result?.code}</p>}
    {/* Správce je adresát té věty „ověří správce" — musí dostat i důvod, ne jen odkaz sám na sebe. */}
    {result?.ok === false && result.detail && <p className="text-zinc-400 text-xs font-mono">{result.detail}</p>}
    {result?.ok && !result.dry_run && <p className="text-green-400">{t('gitlab.pairing.saved', 'Párování ověřeno a uloženo.')}</p>}
    <Button size="sm" disabled={!connection || !entity || !username || (['group','project'].includes(grant) && !target)} loading={mutation.isPending} onClick={() => mutation.mutate(!!preview)}>
      {preview ? t('gitlab.pairing.confirm', 'Potvrdit párování a zvolené pověření') : t('gitlab.pairing.preview', 'Ověřit a zobrazit náhled')}
    </Button>
  </details>;
}
